import React, { Component } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import {
  apiErrorMessage,
  firstError,
  hasErrors,
  validateBrandProduct,
} from "Helpers/ContentValidation";
import {
  brandProductFormToFormData,
  brandProductToFormValues,
  emptyBrandProductForm,
} from "Helpers/ContentPayloads";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card, Grid } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

import BrandProductFormFields from "components/ContentForms/BrandProductFormFields";

export default class UpdateBrandProduct extends Component {
  state = {
    id: "",
    values: emptyBrandProductForm(),
    existingImage: "",
    errors: {},
    brandOptions: [],

    fetching: true,
    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    const id = window.location.pathname.split("/")[2];

    if (!id) {
      this.setState({ fetching: false });
      return this.notify("warning", "No product selected");
    }

    this.setState({ id }, () => {
      this.fetchProduct(id);
      this.fetchBrandOptions();
    });
  }

  fetchProduct = async (id) => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.get(Constants.getUrls.getBrandProduct + id);
      const product = response.data?.data;

      if (!product) throw new Error("Product not found");

      this.setState({
        values: brandProductToFormValues(product),
        existingImage: product.image || "",
        fetching: false,
      });
    } catch (error) {
      this.setState({ fetching: false });
      this.notify("warning", apiErrorMessage(error, "Could not load the product"));
    }
  };

  fetchBrandOptions = async () => {
    try {
      const response = await axios.get(Constants.getUrls.getBrandOptions);
      this.setState({ brandOptions: response.data?.data || [] });
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not load brands"));
    }
  };

  handleChange = (name, value) => {
    this.setState((state) => {
      const errors = { ...state.errors };
      delete errors[name];
      return { values: { ...state.values, [name]: value }, errors };
    });
  };

  handleFile = (file) => {
    this.setState((state) => ({
      values: { ...state.values, image_file: file },
      errors: { ...state.errors, image: undefined },
    }));
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateBrandProduct(this.state.values, {
      isUpdate: true,
      hasImage: Boolean(this.state.existingImage),
    });

    if (hasErrors(errors)) {
      this.setState({ errors });
      return this.notify("warning", firstError(errors));
    }

    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true, errors: {} });

    try {
      const response = await axios.put(
        Constants.putUrls.updateBrandProduct + this.state.id,
        brandProductFormToFormData(this.state.values)
      );

      this.setState({
        loading: false,
        existingImage: response.data?.data?.image || this.state.existingImage,
        values: { ...this.state.values, image_file: null },
      });

      this.notify("success", response.data?.message || "Product updated successfully!");
      setTimeout(() => this.setState({ redirect: true }), 1200);
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not update the product"));
    }
  };

  render() {
    if (this.state.redirect) {
      const brand = this.state.values.brand;
      return (
        <Navigate to={brand ? `/brandproducts?brand=${brand}` : "/brandproducts"} replace />
      );
    }

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Update Brand Product
              </MDTypography>
            </MDBox>

            <MDBox py={3} px={2}>
              {this.state.fetching ? (
                <Grid align="center">
                  <h4>Loading...</h4>
                </Grid>
              ) : (
                <form onSubmit={this.handleSubmit} noValidate>
                  <BrandProductFormFields
                    values={this.state.values}
                    errors={this.state.errors}
                    onChange={this.handleChange}
                    onFile={this.handleFile}
                    brandOptions={this.state.brandOptions}
                    existingImage={this.state.existingImage}
                    isUpdate
                  />

                  <MDBox mt={4} display="flex" gap={2}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      disabled={this.state.loading}
                    >
                      {this.state.loading ? "Updating..." : "Update Product"}
                    </MDButton>

                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={() => this.setState({ redirect: true })}
                      disabled={this.state.loading}
                    >
                      Cancel
                    </MDButton>
                  </MDBox>
                </form>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Brand Products"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
